<script setup>
import {computed, reactive, inject, ref} from "vue";
//import functions
import {required} from "@vuelidate/validators";
//import components
import {Icon} from "@iconify/vue";
import {
  Dialog,
  Checkbox,
  Select, InputText, FloatLabel, InputNumber, Textarea, InputGroup, InputGroupAddon
} from "primevue";

//import stores
import {useCurrencyPricesStore} from "@/stores/currencyPrices.js";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useVuelidate} from "@vuelidate/core";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {showMessage} from "@/utils/showMessage.js";

//stores
const currencyPricesStore = useCurrencyPricesStore();
const ordersStore = useMainOrdersStore()

//loading flags
const {
  saving,
  fetching,
  setSaving,
  setFetching,
} = useLoadingFlags()
const isUploadingAttachment = ref(false)

//other composables
const {isXL} = useBreakpoint();
const toast = useToast();

//emits
const emit = defineEmits(['refresh', 'refreshOrder']);

//props
const props = defineProps({
  hotOrderID: {
    type: Number,
    required: false
  },
  orderType: {
    type: String,
    required: true
  },
  subOrder: {
    type: Object,
    required: true
  }
})

//injects
const visible = inject('isShowPublishModal')

//component variables
const currencyPrices = ref([])
const discordChannels = ref([])
const fetchingDiscordChannels = ref(false)
const fetchingCurrencies = ref(false)
const platforms = ref(['Site'])
const isPostedDiscordAd = ref(false)
const isPostingDiscordAd = ref(false)
const isRepostedDiscordAd = ref(false)
const isRepostingDiscordAd = ref(false)
const isRemovedDiscordAd = ref(false)
const isRemovingDiscordAd = ref(false)
const isShowRefPrice = ref(false)
const refAmount = ref(null)
const refCurrency = ref(null)

const selectedCurrency = computed(() => {
  return currencyPrices.value.find(
      item => item.name === refCurrency.value
  )
})

const hotOrder = reactive({
  "id": props.hotOrderID || props.subOrder?.id,
  "type": props.orderType,
  "discordChannel": "",
  "hidePrice": false
})
const rules = computed(() => {
  return {
    state: {required},
    title: {required},
    rate: {required},
    amount: {required},
    value: {required},
    currency: {required},
    //takerID: {required}
  };
});
const $vuelidate = useVuelidate(rules, hotOrder);

//component functions
const onSave = async () => {

}

const getDiscordChannels = async () => {
  let type;
  if (props.orderType === "Boost") {
    type = 'boost'
  } else if (props.orderType === "CurrencyBuy" || props.orderType === "CurrencySell") {
    type = 'currency'
  }
  fetchingDiscordChannels.value = true
  try {
    const res = await ordersStore.action_getDiscordChannels({orderType: type})
    if (res.result) {
      discordChannels.value = Object.entries(res.data).map(([value, label]) => ({
        label,
        value
      }))
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    fetchingDiscordChannels.value = false
  }
}

const getCurrencies = async () => {
  try {
    fetchingCurrencies.value = true
    const res = await currencyPricesStore.action_getPrices();
    if (res.result === true) {
      currencyPrices.value = res.data;
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
      showMessage(res, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  } finally {
    fetchingCurrencies.value = false
  }
}

const showChannels = async (platforms) => {
  if (platforms.includes('Discord')) {
    await getDiscordChannels()
  } else {
    hotOrder.discordChannel = ""
  }
}

const postDiscordAd = async (postAction) => {
  let type;
  if (props.orderType === "Boost") {
    type = 'boost'
  } else if (props.orderType === "CurrencyBuy" || props.orderType === "CurrencySell") {
    type = 'currency'
  }
  if (postAction === 'post-or-update') {
    isPostingDiscordAd.value = true
  } else if (postAction === 'remove-and-post') {
    isRepostingDiscordAd.value = true
  } else if (postAction === 'remove') {
    isRemovingDiscordAd.value = true
  }

  try {
    const res = await ordersStore.action_postDiscordAd({...hotOrder, "orderType": type, "postAction": postAction})
    if (res.result) {
      if (postAction === 'post-or-update') {
        showMessage(`Published!`, 'success');
        isPostedDiscordAd.value = true
      } else if (postAction === 'remove-and-post') {
        showMessage(`Published!`, 'success');
        isRepostedDiscordAd.value = true
      } else if (postAction === 'remove') {
        showMessage(`Removed!`, 'success');
        isRemovedDiscordAd.value = true
      }
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    if (postAction === 'post-or-update') {
      isPostingDiscordAd.value = false
    } else if (postAction === 'remove-and-post') {
      isRepostingDiscordAd.value = false
    } else if (postAction === 'remove') {
      isRemovingDiscordAd.value = false
    }
  }
}

const showRefAmountFields = async () => {
  if (isShowRefPrice.value) {
    await getCurrencies()
  }
}

const onRefAmountInput = (e) => {
  refAmount.value = e.value
};

const excludeCurrencyPrices = ["Toman Gold Buy Manual", "Toman Gold Buy Auto", "Toman Shop"];
const onChangeCurrency = (currencyName) => {
  const currency = currencyPrices.value.find(
      item => item.name === currencyName
  )

  if (currency?.name === 'Toman') {
    refAmount.value = props.subOrder.amount * Number(currency.price)
  } else {
    refAmount.value = null
  }
}

//hooks

//provides
</script>

<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        Publish Hot Order ({{ hotOrderID }})
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="subOrderTitle" v-model="subOrder.title" autocomplete="off" readonly disabled
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700 p-filled"/>
            <label for="subOrderTitle" class="!bg-slate-900">Title</label>
          </FloatLabel>
        </div>
        <div class="col-span-6 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="subOrderCurrency" v-model="subOrder.currency" autocomplete="off" readonly disabled
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700 p-filled"/>
            <label for="subOrderCurrency" class="!bg-slate-900">Currency</label>
          </FloatLabel>
        </div>
        <div class="col-span-6 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="subOrderAmount" v-model="subOrder.amount" autocomplete="off" readonly disabled
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700 p-filled"/>
            <label for="subOrderAmount" class="!bg-slate-900">Amount</label>
          </FloatLabel>
        </div>
        <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <Textarea v-auto-dir id="subOrderDescription" v-model="subOrder.description" rows="5" readonly disabled
                      style="resize: none"
                      class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700 p-filled"/>
            <label for="subOrderDescription" class="!bg-slate-900">Description</label>
          </FloatLabel>
        </div>
      </div>

<!--      <div class="flex flex-col justify-center gap-4 divide-y divide-slate-700">
        <div class="flex flex-row gap-6">
          <div class="flex items-center gap-2">
            <Checkbox v-model="isShowRefPrice" inputId="refPrice" name="refPrice"
                      @update:modelValue="showRefAmountFields" binary/>
            <label for="refPrice" class="text-slate-400 whitespace-nowrap select-none"> Ref. Price</label>
          </div>
          <div class="flex-1 grid grid-cols-2 gap-6">
            <InputGroup class="col-span-1">
              <FloatLabel variant="on">
                <InputNumber id="refAmount" v-model="refAmount"
                             autocomplete="off" :disabled="!isShowRefPrice"
                             :input-class="['!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700', !isShowRefPrice ? '!opacity-50' : '']"
                             @input="onRefAmountInput"/>
                <label for="refAmount" :class="['!bg-slate-900', !isShowRefPrice ? '!opacity-50' : '']">Ref.
                  Amount</label>
              </FloatLabel>
              <InputGroupAddon
                  :class="['!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px] text-[.7rem] whitespace-nowrap w-auto flex-shrink-0 !px-1', !isShowRefPrice ? '!opacity-50' : '']">
                {{ selectedCurrency?.name }}
              </InputGroupAddon>
            </InputGroup>
            <div class="col-span-1 flex flex-row gap-2 text-xs 2xl:text-sm">
              <div class="flex-1 w-full">
                <FloatLabel variant="on">
                  <Select v-model="refCurrency" input-id="refCurrency" :disabled="!isShowRefPrice"
                          :options="currencyPrices.filter(item => !excludeCurrencyPrices.includes(item.name))"
                          optionLabel="name" optionValue="name" :loading="fetchingCurrencies"
                          :class="['!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700', !isShowRefPrice ? '!opacity-50' : '']"
                          label-class="!text-sm !items-center !flex"
                          @update:modelValue="onChangeCurrency">
                    <template #option="slotProps">
                      <div class="flex flex-row items-center gap-4 justify-between w-full">
                        <span class="text-sm">{{ slotProps.option.name }}</span>
                        <div class="flex flex-row gap-2 items-center">
                          <span class="text-xs">
                            {{ slotProps.option.price }} {{ slotProps.option.name === 'Toman' ? 'Tmn' : '$' }}
                          </span>
                          <div class="h-4 w-[1px] bg-slate-500"></div>
                          <span class="text-[.7rem]">
                            {{ gregorianDate(slotProps.option.lastUpdate, 'DD MMM HH:mm') }}
                          </span>
                        </div>
                      </div>
                    </template>
                  </Select>
                  <label for="refCurrency" :class="['!bg-slate-900', !isShowRefPrice ? '!opacity-50' : '']">Ref.
                    Currency</label>
                </FloatLabel>
              </div>
              <button @click="getCurrencies">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-refresh-cw-icon lucide-refresh-cw">
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                  <path d="M21 3v5h-5"/>
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                  <path d="M8 16H3v5"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>-->

      <div class="flex flex-col gap-4 border border-slate-800 rounded-md p-4">
        <div class="flex flex-row gap-2 justify-between border-b border-slate-700 pb-1">
          <span>Broadcast Channels</span>
        </div>
        <div class="flex flex-col justify-center gap-4 divide-y divide-slate-700">
          <div class="flex flex-row gap-6">
            <div class="flex items-center gap-2">
              <Checkbox v-model="platforms" inputId="platform1" name="platform" value="Site" readonly disabled/>
              <label for="platform1" class="text-slate-400"> Site </label>
            </div>
            <a href="https://boostaminophen.com" class="text-slate-400">(boostaminophen.com)</a>
          </div>
          <div class="flex flex-row gap-6 items-center justify-between pt-4">
            <div class="flex-1 flex flex-row gap-6 items-center">
              <div class="flex items-center gap-2">
                <Checkbox v-model="platforms" inputId="platform3" name="platform" value="Discord"
                          @update:modelValue="showChannels"/>
                <label for="platform3"> Discord </label>
              </div>
              <div class="flex-1 w-full">
                <FloatLabel variant="on" :class="{ 'has-error': $vuelidate.currency.$error }">
                  <Select v-model="hotOrder.discordChannel" input-id="channel" :options="discordChannels"
                          :disabled="!platforms.includes('Discord')"
                          optionLabel="label" optionValue="value"
                          :loading="fetchingDiscordChannels"
                          class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700 min-w-52"
                          :class="{ 'has-error has-addon': $vuelidate.currency.$error, '!opacity-50': !platforms.includes('Discord') }"
                          label-class="!text-sm !items-center !flex">
                    <!--                    <template #option="slotProps">
                                          <div class="flex flex-row items-center gap-4 justify-between w-full">
                                            <span class="text-sm">{{ slotProps.option.name }}</span>
                                            <div class="flex flex-row gap-2 items-center">
                                                <span class="text-xs">
                                                  {{ slotProps.option.price }} {{ slotProps.option.name === 'Toman' ? 'Tmn' : '$' }}
                                                </span>
                                              <div class="h-4 w-[1px] bg-slate-500"></div>
                                              <span class="text-[.7rem]">
                                                  {{ gregorianDate(slotProps.option.lastUpdate, 'DD MMM HH:mm') }}
                                                </span>
                                            </div>
                                          </div>
                                        </template>-->
                  </Select>
                  <label for="channel" :class="['!bg-slate-900', !platforms.includes('Discord') ? '!opacity-50' : '']">Channel</label>
                </FloatLabel>
              </div>
            </div>
            <div class="flex flex-row gap-6 items-center">
              <button v-if="hotOrder.hidePrice" v-tippy="{ content: 'Price is hidden!', theme: 'my-theme' }"
                      @click="hotOrder.hidePrice = !hotOrder.hidePrice" :disabled="!platforms.includes('Discord')"
                      class="group">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-eye-off-icon lucide-eye-off group-disabled:stroke-slate-400">
                  <path
                      d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/>
                  <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/>
                  <path
                      d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/>
                  <path d="m2 2 20 20"/>
                </svg>
              </button>
              <button v-else v-tippy="{ content: 'Price is visible!', theme: 'my-theme' }"
                      @click="hotOrder.hidePrice = !hotOrder.hidePrice"
                      :disabled="!platforms.includes('Discord')" class="group">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-eye-icon lucide-eye group-disabled:stroke-slate-400">
                  <path
                      d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </button>
              <div class="flex flex-row gap-1 items-center">
                <!--              <button @click="postDiscordAd('post-or-update')"
                                      class="btn btn-outline-dark flex items-center justify-center gap-2 disabled:opacity-50 py-1 px-4"
                                      :disabled="!hotOrder.discordChannel">
                                <svg v-if="isPostedDiscordAd" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                     viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                     class="lucide lucide-check-icon lucide-check">
                                  <path d="M20 6 9 17l-5-5"/>
                                </svg>
                                <Icon v-else-if="isPostingDiscordAd" icon="eos-icons:bubble-loading" height="16"/>
                                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                     class="lucide lucide-send-horizontal-icon lucide-send-horizontal">
                                  <path
                                      d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z"/>
                                  <path d="M6 12h16"/>
                                </svg>
                                Post
                              </button>-->
                <button @click="postDiscordAd('remove-and-post')"
                        class="btn btn-outline-dark flex items-center justify-center gap-2 disabled:opacity-50 py-1 px-4"
                        :disabled="!hotOrder.discordChannel || (isShowRefPrice && !(refCurrency && refAmount))">
                  <svg v-if="isRepostedDiscordAd" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                       viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                       class="lucide lucide-check-icon lucide-check">
                    <path d="M20 6 9 17l-5-5"/>
                  </svg>
                  <Icon v-else-if="isRepostingDiscordAd" icon="eos-icons:bubble-loading" height="16"/>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                       class="lucide lucide-send-horizontal-icon lucide-send-horizontal">
                    <path
                        d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z"/>
                    <path d="M6 12h16"/>
                  </svg>
                  Post
                </button>
                <button @click="postDiscordAd('remove')"
                        class="btn btn-outline-dark flex items-center justify-center gap-2 disabled:opacity-50 py-1 px-4"
                        :disabled="!hotOrder.discordChannel">
                  <svg v-if="isRemovedDiscordAd" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                       viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                       class="lucide lucide-check-icon lucide-check">
                    <path d="M20 6 9 17l-5-5"/>
                  </svg>
                  <Icon v-else-if="isRemovingDiscordAd" icon="eos-icons:bubble-loading" height="16"/>
                  <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                       class="lucide lucide-trash2-icon lucide-trash-2">
                    <path d="M10 11v6"/>
                    <path d="M14 11v6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                    <path d="M3 6h18"/>
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                  Remove
                </button>
              </div>
            </div>
          </div>
          <div class="flex flex-row gap-6 items-center justify-between pt-4">
            <div class="flex-1 flex flex-row gap-6 items-center">
              <div class="flex items-center gap-2">
                <Checkbox v-model="platforms" inputId="platform2" name="platform" value="Telegram" readonly disabled/>
                <label for="platform2" class="text-slate-400"> Telegram </label>
              </div>
              <span class="text-slate-400">(coming soon)</span>
            </div>

            <div class="flex flex-row gap-6 items-center">
              <!--            <button v-if="hotOrder.hidePrice" v-tippy="{ content: 'Price is hidden!' }" disabled class="group">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-eye-off-icon lucide-eye-off group-disabled:stroke-slate-400">
                  <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/>
                  <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/>
                  <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/>
                  <path d="m2 2 20 20"/>
                </svg>
              </button>-->
              <button v-tippy="{ content: 'Price is visible!' }" disabled class="group">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-eye-icon lucide-eye group-disabled:stroke-slate-400">
                  <path
                      d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </button>
              <div class="flex flex-row gap-1 items-center">
                <div class="flex flex-row gap-1 items-end self-end justify-self-end">
                  <button
                      class="btn btn-outline-dark flex items-center justify-center gap-2 disabled:opacity-50 py-1 px-4"
                      disabled>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="lucide lucide-send-horizontal-icon lucide-send-horizontal">
                      <path
                          d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z"/>
                      <path d="M6 12h16"/>
                    </svg>
                    Post
                  </button>
                  <!--                <button
                                      class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1 px-4"
                                      disabled>
                                    Repost
                                  </button>-->
                  <button
                      class="btn btn-outline-dark flex items-center justify-center gap-2 disabled:opacity-50 py-1 px-4"
                      disabled>
                    <svg
                        width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="lucide lucide-trash2-icon lucide-trash-2">
                      <path d="M10 11v6"/>
                      <path d="M14 11v6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                      <path d="M3 6h18"/>
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-end gap-3">
      <button @click="onSave"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1" disabled>
        Post All
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
      </button>
    </div>
  </Dialog>
</template>

<style>
.tippy-box[data-theme~='my-theme'] {
  @apply bg-slate-900 text-sm p-2 text-white;
}
</style>